import React from 'react'
import styled from 'styled-components'
import moment from 'moment-timezone'
import {useLocalization} from 'lib/LocalizationProvider'
import {Agenda} from 'Event/template/Panels/Dashboard/AgendaList'

export default function EventTimes<T extends Agenda>(props: {agenda: T}) {
  const start = moment(props.agenda.startDate).tz(moment.tz.guess())
  const getMonth = (date: moment.Moment) => date.format('MMMM')
  const getDay = (date: moment.Moment) => date.format('Do')

  const {has12HourTime} = useLocalization()
  const timeFormat = has12HourTime ? 'h:mma' : 'HH:mm'
  const getTime = (date: moment.Moment) => date.format(timeFormat)

  const getTimezone = (date: moment.Moment) => date.format('z')

  const tz = getTimezone(start)

  if (!props.agenda.startDate) return null

  /**
   * Single start date only
   */
  if (
    !props.agenda.endDate ||
    props.agenda.startDate === props.agenda.endDate
  ) {
    return (
      <TimeText aria-label="agenda event times">
        {`${getMonth(start)} ${getDay(start)}:`} {`${getTime(start)} ${tz}`}
      </TimeText>
    )
  }

  /**
   * Start, and end same day, different times
   */
  const end = moment(props.agenda.endDate).tz(moment.tz.guess())
  const sameMonth = getMonth(end) === getMonth(start)
  const sameDay = getDay(end) === getDay(start)
  if (sameMonth && sameDay) {
    return (
      <TimeText aria-label="agenda event times">
        {`${getMonth(start)} ${getDay(start)}:`} {getTime(start)}
        {` - ${getTime(end)} ${tz}`}
      </TimeText>
    )
  }

  /**
   * Two different date/times
   */
  return (
    <TimeText aria-label="agenda event times">
      {`${getMonth(start)} ${getDay(start)}:`} {getTime(start)}
      {` - `}
      {getMonth(end)} {getDay(end)}: {getTime(end)} {tz}
    </TimeText>
  )
}

const TimeText = styled.div`
  font-size: 16px;
`
